import { useCallback, useEffect, useState } from "react";

/**
 * Borradores locales del panel de Comunidad: lo que se está escribiendo en un formulario (una
 * edición del newsletter, una novedad) se guarda solo en este navegador mientras se escribe,
 * y se recupera si se recarga la página, se publica o se cierra la pestaña sin querer. Se
 * borra recién cuando el formulario se guarda de verdad en el servidor.
 *
 * Es un respaldo de este navegador, no un guardado: el borrador real sigue siendo el del
 * servidor ("Agregar edición" / "Guardar").
 */
const PREFIX = "comunidad-admin-draft:";

const read = <T,>(key: string): T | null => {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* sin almacenamiento (modo privado, cuota llena): sigue funcionando sin respaldo */
  }
};

export const clearLocalDraft = (key: string) => {
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    /* idem */
  }
};

export const readLocalDraft = read;
export const saveLocalDraft = write;

/** ¿El formulario tiene algo escrito? (cualquier campo de texto no vacío). */
export const hasContent = (value: unknown): boolean =>
  !!value &&
  typeof value === "object" &&
  Object.values(value as Record<string, unknown>).some((v) => typeof v === "string" && v.trim() !== "");

/** ¿Hay algún formulario del panel con texto sin guardar en el servidor? */
export function hasPendingLocalDrafts(): boolean {
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(PREFIX) && hasContent(read(k.slice(PREFIX.length)))) return true;
    }
  } catch {
    /* idem */
  }
  return false;
}

/**
 * Como `useState`, pero respaldado en este navegador bajo `key`. `clear()` vuelve al valor
 * vacío y borra el respaldo (llamarlo cuando el servidor confirmó el guardado).
 */
export function useLocalDraft<T extends object>(key: string, empty: T): [T, (v: T | ((prev: T) => T)) => void, () => void] {
  const [value, setValue] = useState<T>(() => ({ ...empty, ...(read<T>(key) ?? {}) }));

  useEffect(() => {
    if (hasContent(value)) write(key, value);
    else clearLocalDraft(key);
  }, [key, value]);

  const clear = useCallback(() => {
    clearLocalDraft(key);
    setValue(empty);
  }, [key, empty]);

  return [value, setValue, clear];
}

/** Avisa antes de cerrar o recargar la pestaña si hay algo escrito sin guardar. */
export function useWarnBeforeLeaving(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
}
